import React, { useCallback, useEffect, useState } from "react";
import { Target, RefreshCw, TrendingUp, Wallet, Flag, Percent, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { api, fmtINR } from "../lib/api";
import { TARGET_STATUS_META } from "./SupervisorTargetDialog";

// Progress indicator colour follows the pace status (bar is a Radix Progress; indicator is its first child).
const BAR_CLS = {
  achieved: "[&>div]:bg-emerald-600",
  on_track: "[&>div]:bg-primary",
  behind: "[&>div]:bg-amber-500",
  no_target: "[&>div]:bg-muted-foreground/40",
};

function StatTile({ icon: Icon, label, value, sub, tone = "", testId }) {
  return (
    <div className="rounded-lg border bg-card px-3 py-2.5" data-testid={testId}>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <p className={`text-base font-semibold tabular-nums mt-0.5 ${tone}`} data-testid={`${testId}-value`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

/**
 * Phase 139 — Tax Collector's own "Target vs Collection" card for the current month.
 * Reads GET /t/{code}/targets/me: Admin/Supervisor-assigned monthly target (Phase 81 data) vs the
 * collector's credited receipts this month (same unified rule as the admin scorecard), with pacing.
 */
export function CollectorTargetCard({ code }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get(`/t/${code}/targets/me`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load your monthly target"))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card className="shadow-sm" data-testid="collector-target-loading">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-2 w-full rounded-full" />
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-destructive/30" data-testid="collector-target-error">
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Target vs collection</p>
            <p className="text-xs text-destructive mt-0.5">{String(error)}</p>
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="collector-target-retry">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const target = data.target_amount;
  const collected = data.collected_mtd || 0;
  const hasTarget = !!target && target > 0;
  const status = hasTarget ? (data.status || "no_target") : "no_target";
  const meta = TARGET_STATUS_META[status] || TARGET_STATUS_META.no_target;
  const pct = data.achievement_pct;
  const bar = hasTarget ? Math.min(100, Math.max(0, Math.round(collected * 100 / target))) : 0;
  const expected = data.expected_to_date;
  // Pace figures are pro-rata estimates — show whole rupees so the card reads cleanly on a phone.
  const gap = hasTarget && expected != null ? Math.round(collected - expected) : null;

  let paceLine = null;
  if (hasTarget) {
    if (status === "achieved") {
      paceLine = `Target achieved — ${fmtINR(Math.max(Math.round(collected - target), 0))} above target`;
    } else if (gap != null && gap < 0) {
      paceLine = `${fmtINR(Math.abs(gap))} behind pace`;
    } else if (gap != null) {
      paceLine = `${fmtINR(gap)} ahead of pace`;
    }
  }

  return (
    <Card className="shadow-sm border-l-4 border-l-primary" data-testid="collector-target-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" /> Target vs collection
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="collector-target-month">
              {data.month_label} • Day {data.elapsed_days} of {data.days_in_month}
              {data.days_left > 0 ? ` • ${data.days_left} day${data.days_left === 1 ? "" : "s"} left` : ""}
            </p>
          </div>
          <Badge variant="outline" className={`text-[10px] shrink-0 ${meta.cls}`} data-testid="collector-target-status">
            {hasTarget ? meta.label : "No target"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        {hasTarget ? (
          <>
            <div>
              <p className="text-2xl font-semibold font-heading tabular-nums" data-testid="collector-target-headline">
                {fmtINR(collected)}
                <span className="text-sm font-normal text-muted-foreground"> of {fmtINR(target)}</span>
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Progress value={bar} className={`h-2.5 flex-1 ${BAR_CLS[status] || BAR_CLS.no_target}`}
                  aria-label="Monthly target progress" data-testid="collector-target-progress" />
                <span className="text-xs font-semibold tabular-nums w-14 text-right" data-testid="collector-target-pct">
                  {pct != null ? `${pct}%` : "0%"}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <StatTile icon={Flag} label="Monthly target" value={fmtINR(target)}
                sub={data.set_by_name ? `Set by ${data.set_by_name}` : undefined} testId="collector-target-amount" />
              <StatTile icon={Wallet} label="Collected this month" value={fmtINR(collected)}
                sub={`${data.receipts || 0} receipt${data.receipts === 1 ? "" : "s"}`} testId="collector-target-collected" />
              <StatTile icon={TrendingUp} label="Remaining" value={fmtINR(data.remaining ?? Math.max(target - collected, 0))}
                tone={(data.remaining ?? 0) > 0 ? "text-destructive" : "text-emerald-600"}
                sub={(data.remaining ?? 0) > 0 ? "still to collect" : "nothing left — well done"} testId="collector-target-remaining" />
              <StatTile icon={Percent} label="Achievement" value={pct != null ? `${pct}%` : "0%"}
                tone={status === "achieved" ? "text-emerald-600" : status === "behind" ? "text-amber-600" : ""}
                sub={paceLine || undefined} testId="collector-target-achievement" />
            </div>

            {expected != null && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="collector-target-expected">
                <Info className="h-3 w-3 shrink-0" />
                Expected by today: <span className="font-semibold tabular-nums text-foreground">{fmtINR(Math.round(expected))}</span>
                {data.collected_today > 0 && <> • Today {fmtINR(data.collected_today)}</>}
              </p>
            )}
          </>
        ) : (
          <div data-testid="collector-target-empty">
            <p className="text-sm font-medium">No target assigned yet for {data.month_label}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Your Admin or Supervisor sets monthly collection targets under Team. Your collections are still being counted.
            </p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <StatTile icon={Wallet} label="Collected this month" value={fmtINR(collected)}
                sub={`${data.receipts || 0} receipt${data.receipts === 1 ? "" : "s"}`} testId="collector-target-collected" />
              <StatTile icon={Flag} label="Monthly target" value="—" sub="not assigned" testId="collector-target-amount" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
